<script lang="ts">
	import { Stack, Select, Text } from '@Shelly/shelly-design-system';
	import { devices } from '../../../stores';
	import {
		getDeviceSettings,
		getDeviceStatus,
		readDeviceSettings,
		readDeviceStatus,
	} from '../../../stores/device-runtime/status-selectors';
	import { get } from 'svelte/store';
	import { flowEditorState } from '../../../stores/flow_editor';
	import { _deepClone } from '../../../helpers/utilities';
	import { _init_conditions_components } from '../../Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/common-helpers';
	import { selectDeviceConditions } from '../../Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/common-helpers';
	import InlineDeviceSelector from './Shared/InlineDeviceSelector.svelte';
	import ChannelSelector from './Shared/ChannelSelector.svelte';
	import { resolveDeviceInStore } from '../helpers/resolveDeviceInStore';

	export let nodeId: string;
	export let variant: 'device' | 'variable' | 'hysteresis' = 'device';

	// ── Local state ────────────────────────────────────────────────────────────

	let selectedDevice: string | null = null;
	let selectedChannel: number = 0;
	let selectedProperty: string = '';
	let components: any[] = [];

	/** Metadata for the currently selected property (unit, min, max, hasValues). */
	let propertyMeta: {
		unit: string;
		min: number;
		max: number;
		hasValues: boolean;
		values: string[];
	} = { unit: '', min: 0, max: 100, hasValues: false, values: [] };

	// ── Initialization guard ───────────────────────────────────────────────────

	let lastInitNodeId = '';
	let deferredDeviceId: string | null = null;

	$: node = $flowEditorState.nodes.find((n) => n.id === nodeId);
	$: isVariable = variant === 'variable';
	$: isHysteresis = variant === 'hysteresis';
	$: variableName = String(node?.data.variableName || '');
	$: if (node && node.id !== lastInitNodeId) {
		lastInitNodeId = node.id;
		initializeFromNode();
	}

	function initializeFromNode() {
		if (!node) return;
		selectedDevice = (node.data.deviceId as string) || null;
		selectedChannel = parseInt(String(node.data.channel ?? '0')) || 0;
		selectedProperty =
			typeof node.data.property === 'string'
				? node.data.property
				: String((node.data.property as { id?: string } | undefined)?.id || '');
		if (selectedDevice) {
			loadComponents(selectedDevice);
			deferredDeviceId =
				selectedDevice && !resolveDeviceInStore($devices, selectedDevice, selectedChannel)
					? selectedDevice
					: null;
		} else {
			deferredDeviceId = null;
		}
	}

	$: if (
		deferredDeviceId &&
		resolveDeviceInStore($devices, deferredDeviceId, selectedChannel) &&
		lastInitNodeId === node?.id
	) {
		loadComponents(deferredDeviceId);
		deferredDeviceId = null;
	}

	function loadComponents(deviceId: string) {
		const device = resolveDeviceInStore(get(devices), deviceId, selectedChannel);
		if (!device) {
			components = [];
			return;
		}
		const status = readDeviceStatus(device.id ?? deviceId);
		const settings = readDeviceSettings(device.id ?? deviceId);
		try {
			components = _init_conditions_components(device, status, settings);
		} catch (e) {
			components = [];
		}
		updatePropertyMeta();
	}

	// Genuine device state values that warrant a dropdown (on/off, open/closed, etc.)
	const DEVICE_STATE_VALUES = new Set([
		'on',
		'off',
		'true',
		'false',
		'open',
		'closed',
		'enabled',
		'disabled',
		'locked',
		'unlocked',
		'day',
		'night',
		'home',
		'away',
		'heat',
		'cool',
		'auto',
		'fan_only',
		'dry',
		'single_push',
		'double_push',
		'triple_push',
		'long_push',
	]);

	// Operator names that the old condition wizard embeds as property values.
	const OPERATOR_LIKE_VALUES = new Set([
		'less_than',
		'more_than',
		'equal',
		'not_equal',
		'greater_than',
		'less_or_equal',
		'greater_or_equal',
		'above',
		'below',
		'threshold',
		'between',
	]);

	function isDiscreteEnum(values: string[]): boolean {
		if (!values || values.length === 0) return false;
		// Only treat as enum when ALL values are real device states AND none look like operators.
		// Numeric properties (brightness, power, temp) will have operator names as their
		// "values" in the old wizard — those should show a number input instead.
		return values.every((v) => {
			const lower = String(v).toLowerCase();
			return DEVICE_STATE_VALUES.has(lower) && !OPERATOR_LIKE_VALUES.has(lower);
		});
	}

	function updatePropertyMeta() {
		if (!selectedProperty) {
			propertyMeta = { unit: '', min: 0, max: 100, hasValues: false, values: [] };
			return;
		}
		const comp = components.find((el) => {
			const elValue =
				el.props.add_info_ind !== undefined
					? `${el.props.id}${el.props.add_info_ind}`
					: el.props.input_index !== undefined
						? `${el.props.id}${el.props.input_index}`
						: el.props.id;
			return elValue === selectedProperty;
		});
		if (!comp) {
			propertyMeta = { unit: '', min: 0, max: 100, hasValues: false, values: [] };
			return;
		}
		const rawValues: string[] = comp.props?.values ?? [];
		propertyMeta = {
			unit: comp.props?.character || '',
			min: comp.props?.min ?? 0,
			max: comp.props?.max ?? 100,
			// Only treat as enum if values are actual state values (on/off), not operator names
			hasValues: isDiscreteEnum(rawValues),
			values: rawValues,
		};
	}

	// ── Derived ────────────────────────────────────────────────────────────────

	$: currentDevice = selectedDevice
		? resolveDeviceInStore($devices, selectedDevice, selectedChannel)
		: null;
	$: selectedStatusStore = getDeviceStatus(selectedDevice || undefined);
	$: selectedSettingsStore = getDeviceSettings(selectedDevice || undefined);
	$: if (selectedDevice && currentDevice && lastInitNodeId === node?.id) {
		$selectedStatusStore;
		$selectedSettingsStore;
		loadComponents(selectedDevice);
	}
	$: availableChannels = (currentDevice as any)?.channels || [0];

	$: propertyOptions = selectDeviceConditions(components);

	$: cases = ((node?.data.cases as any[]) ?? []) as Array<{
		id: string;
		label: string;
		operator: string;
		value: string;
		value2?: string;
	}>;

	// ── Operator options — restricted by property type ─────────────────────────

	$: operatorOptions = propertyMeta.hasValues
		? [
				{ value: 'eq', title: '= equals' },
				{ value: 'ne', title: '≠ not equal' },
			]
		: [
				{ value: 'eq', title: '= equals' },
				{ value: 'ne', title: '≠ not equal' },
				{ value: 'gt', title: '> greater than' },
				{ value: 'ge', title: '≥ greater or equal' },
				{ value: 'lt', title: '< less than' },
				{ value: 'le', title: '≤ less or equal' },
				{ value: 'between_inc', title: '[ ] between (inclusive)' },
				{ value: 'between_exc', title: '( ) between (exclusive)' },
			];

	// ── State commit helpers ───────────────────────────────────────────────────

	function commitPatch(patch: Record<string, unknown>) {
		if (!node) return;
		flowEditorState.updateNode(node.id, {
			data: { ...node.data, ...patch } as any,
		});
	}

	// ── Device selection ───────────────────────────────────────────────────────

	function handleDeviceSelect(event: CustomEvent) {
		const deviceRef = event.detail;
		if (!deviceRef) {
			selectedDevice = null;
			selectedChannel = 0;
			components = [];
			selectedProperty = '';
			commitPatch({ deviceId: null, deviceName: '', channel: '0', property: '' });
			return;
		}
		const dev = get(devices)[deviceRef.id];
		selectedDevice = deviceRef.id;
		selectedChannel = 0;
		selectedProperty = '';
		loadComponents(deviceRef.id);
		commitPatch({
			deviceId: deviceRef.id,
			deviceName: dev?.name || deviceRef.id,
			channel: '0',
			property: '',
		});
	}

	function handleChannelChange(event: CustomEvent) {
		selectedChannel = event.detail as number;
		if (selectedDevice) loadComponents(selectedDevice);
		commitPatch({ channel: String(selectedChannel) });
	}

	// ── Property selection ─────────────────────────────────────────────────────

	function handlePropertyChange(event: CustomEvent) {
		const detail = event.detail;
		const val: string = typeof detail === 'string' ? detail : (detail?.value ?? '');
		if (!val) return;

		// Find the matching component to extract cond_name parts
		const comp = components.find((el) => {
			const elValue =
				el.props.add_info_ind !== undefined
					? `${el.props.id}${el.props.add_info_ind}`
					: el.props.input_index !== undefined
						? `${el.props.id}${el.props.input_index}`
						: el.props.id;
			return elValue === val;
		});

		selectedProperty = val;
		updatePropertyMeta();

		if (comp) {
			commitPatch({
				property: comp.props.id,
				propertyUnit: comp.props.character || '',
				add_info_ind: comp.props.add_info_ind,
				input_index: comp.props.input_index,
				devMode: comp.props.devMode,
				propertyDisplayValue: val,
			});
		} else {
			commitPatch({ property: val, propertyDisplayValue: val, propertyUnit: '' });
		}
	}

	// ── Case management ────────────────────────────────────────────────────────

	function addCase() {
		const newCase = {
			id: `c${Date.now()}`,
			label: `Case ${cases.length + 1}`,
			operator: 'eq',
			value: '',
		};
		commitPatch({ cases: [...cases, newCase] });
	}

	function removeCase(index: number) {
		commitPatch({ cases: cases.filter((_, i) => i !== index) });
	}

	function updateCaseField(index: number, field: string, value: string) {
		const updated = cases.map((c, i) => (i === index ? { ...c, [field]: value } : c));
		// If operator changes away from between, clear value2
		if (field === 'operator' && !value.startsWith('between')) {
			updated[index] = { ...updated[index], value2: undefined };
		}
		commitPatch({ cases: updated });
	}

	function isBetween(op: string): boolean {
		return op === 'between_inc' || op === 'between_exc';
	}

	function inputVal(e: Event): string {
		return (e.target as HTMLInputElement).value;
	}

	function selectVal(e: Event): string {
		return (e.target as HTMLSelectElement).value;
	}

	function updateVariableName(event: Event) {
		commitPatch({ variableName: inputVal(event) });
	}

	function updateThreshold(field: 'lowerThreshold' | 'upperThreshold', event: Event) {
		commitPatch({ [field]: Number(inputVal(event)) });
	}

	// For enum properties, build value options from component's values array
	$: enumValueOptions = propertyMeta.hasValues
		? propertyMeta.values.map((v) => ({
				value: v,
				title: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
			}))
		: [];
</script>

<Stack gap="s">
	<Text variant="body" size="m"
		>{isHysteresis ? 'Hysteresis Condition' : isVariable ? 'Variable Switch' : 'Switch / Case'}</Text
	>
	<p class="hint">
		{#if isHysteresis}
			Run the true branch above the upper threshold and the false branch below the lower threshold.
			Values between them do nothing, preventing rapid switching.
		{:else if isVariable}
			Evaluate a Flow variable top-to-bottom. The first matching case runs; unmatched values use
			<em>default</em>.
		{:else}
			Watch a device property reactively. Each case is evaluated top-to-bottom — first match wins.
			Unmatched cases fall to <em>default</em>.
		{/if}
	</p>

	<!-- ── Source: Device ─────────────────────────────────────────────────── -->
	<div class="section-label">Source</div>

	{#if isVariable}
		<label class="field-label" for="variable-switch-name">Variable name</label>
		<input
			id="variable-switch-name"
			class="text-input"
			type="text"
			placeholder="e.g. mode"
			value={variableName}
			on:input={updateVariableName}
		/>
	{:else}
		<InlineDeviceSelector
			selectedDevice={selectedDevice
				? { id: selectedDevice, name: '', channel: String(selectedChannel) }
				: undefined}
			on:select={handleDeviceSelect}
		/>
	{/if}

	{#if selectedDevice && !isVariable}
		<ChannelSelector
			channels={availableChannels}
			{selectedChannel}
			on:change={handleChannelChange}
		/>

		{#if propertyOptions.length > 0}
			<Select
				aria-label="select"
				id="sw-prop"
				icon=""
				label="Property to watch"
				options={propertyOptions}
				value={String(node?.data.propertyDisplayValue ?? selectedProperty)}
				on:change={handlePropertyChange}
			/>
		{:else}
			<p class="hint muted">No properties available for this device.</p>
		{/if}
	{/if}

	<!-- ── Cases ─────────────────────────────────────────────────────────── -->
	{#if isHysteresis && selectedDevice && selectedProperty}
		<div class="section-label" style="margin-top: 8px;">Thresholds</div>
		<div class="threshold-grid">
			<label for="hyst-upper">Turn ON above</label>
			<div class="value-with-unit">
				<input
					id="hyst-upper"
					type="number"
					min={propertyMeta.min}
					max={propertyMeta.max}
					value={String(node?.data.upperThreshold ?? 22)}
					on:input={(event) => updateThreshold('upperThreshold', event)}
				/>
				{#if propertyMeta.unit}<span>{propertyMeta.unit}</span>{/if}
			</div>
			<label for="hyst-lower">Turn OFF below</label>
			<div class="value-with-unit">
				<input
					id="hyst-lower"
					type="number"
					min={propertyMeta.min}
					max={propertyMeta.max}
					value={String(node?.data.lowerThreshold ?? 20)}
					on:input={(event) => updateThreshold('lowerThreshold', event)}
				/>
				{#if propertyMeta.unit}<span>{propertyMeta.unit}</span>{/if}
			</div>
		</div>
		{#if Number(node?.data.lowerThreshold) >= Number(node?.data.upperThreshold)}
			<p class="hint threshold-error">The OFF threshold must be lower than the ON threshold.</p>
		{/if}
	{:else if (isVariable && variableName) || (!isVariable && selectedDevice && selectedProperty)}
		<div class="section-label" style="margin-top: 8px;">Cases</div>

		<div class="cases-list">
			{#each cases as c, i (c.id)}
				<div class="case-card">
					<div class="case-header-row">
						<span class="case-number">Case {i + 1}</span>
						<button class="remove-btn" title="Remove" on:click={() => removeCase(i)}>
							<i class="fa fa-times"></i>
						</button>
					</div>

					<div class="case-condition-row">
						<!-- Operator -->
						<select
							class="op-select"
							value={c.operator}
							on:change={(e) => updateCaseField(i, 'operator', selectVal(e))}
						>
							{#each operatorOptions as opt}
								<option value={opt.value}>{opt.title}</option>
							{/each}
						</select>

						<!-- Value — enum select for true discrete state values (on/off), else number input -->
						{#if propertyMeta.hasValues && !isBetween(c.operator)}
							<select
								class="val-select"
								value={c.value}
								on:change={(e) => updateCaseField(i, 'value', selectVal(e))}
							>
								<option value="">-- pick --</option>
								{#each enumValueOptions as opt}
									<option value={opt.value}>{opt.title}</option>
								{/each}
							</select>
						{:else}
							<input
								class="val-input"
								type="number"
								placeholder={propertyMeta.unit ? propertyMeta.unit : 'value'}
								min={propertyMeta.min}
								max={propertyMeta.max}
								value={c.value}
								on:input={(e) => updateCaseField(i, 'value', inputVal(e))}
							/>
						{/if}

						<!-- Value2 for between operators -->
						{#if isBetween(c.operator)}
							<span class="between-sep">–</span>
							<input
								class="val-input"
								type="number"
								placeholder="max"
								min={propertyMeta.min}
								max={propertyMeta.max}
								value={c.value2 ?? ''}
								on:input={(e) => updateCaseField(i, 'value2', inputVal(e))}
							/>
						{/if}
					</div>
				</div>
			{/each}

			<!-- Default sentinel -->
			<div class="default-sentinel">
				<i class="fa fa-ellipsis-h"></i>
				<span>default</span>
				<span class="default-note">(always fires if no case matches)</span>
			</div>
		</div>

		<button class="add-case-btn" on:click={addCase}>
			<i class="fa fa-plus"></i> Add Case
		</button>
	{:else if selectedDevice && !isVariable}
		<p class="hint muted">Select a property above to configure cases.</p>
	{:else if isVariable}
		<p class="hint muted">Enter a variable name above to configure cases.</p>
	{/if}
</Stack>

<style>
	.hint {
		margin: 0;
		font-size: 0.75rem;
		color: #6b7280;
		line-height: 1.4;
	}

	.hint.muted {
		font-style: italic;
		padding: 4px 0;
	}

	.section-label {
		font-size: 0.7rem;
		font-weight: 600;
		color: #9ca3af;
		text-transform: uppercase;
		letter-spacing: 0.5px;
		margin-bottom: 2px;
	}

	.field-label {
		font-size: 0.75rem;
		color: var(--flow-slot-strong, #334155);
	}
	.text-input,
	.threshold-grid input {
		width: 100%;
		min-height: 32px;
		box-sizing: border-box;
		border: 1px solid rgba(148, 163, 184, 0.45);
		border-radius: 6px;
		background: var(--color-bg, transparent);
		color: inherit;
		padding: 0 8px;
	}
	.threshold-grid {
		display: grid;
		grid-template-columns: minmax(0, 1fr) 8rem;
		align-items: center;
		gap: 8px;
		font-size: 0.75rem;
	}
	.value-with-unit {
		display: flex;
		align-items: center;
		gap: 5px;
	}
	.value-with-unit span {
		color: var(--flow-slot-muted, #64748b);
	}
	.threshold-error {
		color: #dc2626;
	}

	/* ── Cases ────────────────────────────────────────────────────────────── */

	.cases-list {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.case-card {
		background: rgba(232, 121, 249, 0.05);
		border: 1px solid rgba(232, 121, 249, 0.2);
		border-radius: 8px;
		padding: 8px;
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.case-header-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 2px;
	}

	.case-number {
		font-size: 0.7rem;
		font-weight: 600;
		color: #e879f9;
		letter-spacing: 0.3px;
		text-transform: uppercase;
	}

	.remove-btn {
		background: transparent;
		border: none;
		color: #4b5563;
		cursor: pointer;
		padding: 4px 6px;
		border-radius: 4px;
		flex-shrink: 0;
		transition: color 0.15s;
	}

	.remove-btn:hover {
		color: #ef4444;
	}

	.case-condition-row {
		display: flex;
		align-items: center;
		gap: 6px;
		flex-wrap: wrap;
	}

	.op-select {
		background: rgba(255, 255, 255, 0.06);
		border: 1px solid rgba(232, 121, 249, 0.3);
		border-radius: 5px;
		color: #e879f9;
		padding: 3px 6px;
		font-size: 0.75rem;
		cursor: pointer;
		flex-shrink: 0;
	}

	.val-input,
	.val-select {
		flex: 1;
		min-width: 60px;
		height: 28px;
		background: rgba(255, 255, 255, 0.06);
		border: 1px solid rgba(255, 255, 255, 0.15);
		border-radius: 5px;
		color: #e2e8f0;
		padding: 0 8px;
		font-size: 0.78rem;
		font-family: 'Courier New', monospace;
	}

	.between-sep {
		color: #6b7280;
		font-size: 0.8rem;
		flex-shrink: 0;
	}

	.default-sentinel {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 6px 8px;
		border: 1px dashed rgba(255, 255, 255, 0.1);
		border-radius: 6px;
		color: #6b7280;
		font-size: 0.75rem;
	}

	.default-note {
		font-size: 0.68rem;
		color: #4b5563;
		font-style: italic;
	}

	.add-case-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		width: 100%;
		padding: 7px 12px;
		background: rgba(232, 121, 249, 0.08);
		border: 1px dashed rgba(232, 121, 249, 0.4);
		border-radius: 7px;
		color: #e879f9;
		font-size: 0.8rem;
		cursor: pointer;
		transition: background 0.15s;
	}

	.add-case-btn:hover {
		background: rgba(232, 121, 249, 0.15);
	}
</style>
